import { createRouter, createWebHistory } from "vue-router";

import { finishRouteLoading, startRouteLoading } from "@/composables/useRouteLoading";
import { isCloud } from "@/environment";
import { SessionsHandler } from "@/handlers/sessionsHandler";
import { initializeGTM } from "@/init";
import { natsConnect } from "@/nats";
import { captureExceptionWithContext } from "@/sentry";
import wsconnect from "@/server";
import AuthService from "@/services/auth.service";
import { useAppStore } from "@/stores";

import routerOptions from "./routes";

let appRouter = null;

// Current route for non-component callers (e.g. createHandlerContext), reading
// the same currentRoute that useRoute()/$route expose inside components.
export function getCurrentRoute() {
  return appRouter?.currentRoute?.value ?? null;
}

export function createAppRouter(pinia, { history } = {}) {
  const router = createRouter({
    history: history || createWebHistory(),
    routes: routerOptions.routes,
    scrollBehavior: routerOptions.scrollBehavior,
  });
  appRouter = router;

  const getStore = () => useAppStore(pinia);

  // Show a top loading bar while navigation (incl. async guards) is in flight
  router.beforeEach(() => {
    startRouteLoading();
  });

  // Apply the feature flag (?feature=) BEFORE the auth/connection + editSession
  // guards so they see it on the first pass. Otherwise the NATS-connection force
  // and get_session read the flag off, route WAMP, then redirect — a wasted WAMP
  // call that breaks NATS-only testing.
  router.beforeEach((to, from) => preserveQueryFeature(to, from));

  router.beforeEach(async (to) => {
    const store = getStore();

    if (isAuthRoute(to)) {
      // Open the WAMP + NATS connections to authenticate the user. Both are
      // gated: NATS is critical infrastructure, so a failure to connect blocks
      // the route (redirect to login) rather than silently degrading to WAMP.
      try {
        await openConnection(store);
      } catch (error) {
        // Report before recovering: Sentry must fire everywhere, even on-prem /
        // telemetry-disabled. A WAMP or (now-gated) NATS connect failure that
        // reaches here is unexpected — capture it, then keep the login redirect.
        captureExceptionWithContext(error, { connection: "auth" });
        return { name: "login" };
      }

      if (!isAuthRouteAllowed(to, store)) {
        return defaultRoute(router, to, store);
      }
    }

    // reloading the 403 page -> try to go to the original path first
    if (to.name === "403" && to.query.path && !to.redirectedFrom) {
      return { path: to.query.path };
    }

    if (!isGeneralRouteAllowed(to, store)) {
      return defaultRoute(router, to, store);
    }

    // Passed all checks - navigate to target route
    // In Vue Router 4, returning undefined or true means proceed
    return true;
  });

  router.beforeEach(async (to) => {
    if (to.name !== "editSession") {
      return;
    }

    const store = getStore();
    const sessionId = to.query.id;

    if (!sessionId) {
      return { name: "home" };
    }

    // Mirror createHandlerContext inline (avoids a router<->stores/context import
    // cycle): createRpcBackend reads context.natsConnection (camelCase) + route, but
    // the raw store exposes `natsconnection` (lowercase) and no route — passing it
    // pinned get_session to a WAMP-only backend regardless of the nats-piadmin flag.
    const sessionsHandler = new SessionsHandler({
      company: store.company,
      session: store.session,
      natsConnection: store.natsconnection,
      natsScheme: store.natsScheme,
      user: store.user,
      // `to`, not getCurrentRoute(): inside a beforeEach guard the router's
      // current route is still the previous one (or START on a direct load), so
      // the ?feature=nats-piadmin flag would read off. `to` is the route being
      // entered and carries the query.
      route: to,
    });
    const session = await sessionsHandler.getSession(sessionId);

    // TODO: check if this works properly
    if (!session?.editable) {
      // User doesn't have permission - redirect silently
      // (users typically arrive here from pi-web RTE Cockpit link)
      return { name: "home" };
    }
  });

  // Preserve preview environment params (project, mr) in query
  router.beforeEach((to, from) => preserveQueryProject(to, from));

  // Add query parameter to first navigation from external source
  router.beforeEach((to, from) => markExternalFrom(to, from));

  // Hide the loading bar once navigation settles (success or failure)
  router.afterEach(() => {
    finishRouteLoading();
  });
  router.onError(() => {
    finishRouteLoading();
  });

  // Change document.title based on the current page
  router.afterEach((to, from) => {
    if (to.name !== from.name) {
      document.title = routeName(to);
    }
  });

  // Load GTM only on routes explicitly opted in via `meta.gtm`.
  // Admin-side usage of RTE Cockpit is not tracked in Google Analytics.
  router.afterEach((to) => {
    if (!to.meta?.gtm) return;
    initializeGTM();
  });

  // Helper to check if route is allowed (for sidebar filtering)
  router.isRouteAllowed = (route) => {
    const store = getStore();
    return isRouteAllowedFull(route, store);
  };

  return router;
}

function routeName(route) {
  if (route.name === "register") {
    return "piplanning.io - Register";
  }
  return "RTE Cockpit - " + (route.meta?.title || route.name);
}

function defaultRoute(router, to, store) {
  const defaults = router.options.routes.filter(
    (route) => route.meta?.default !== undefined && route.meta?.default !== null,
  );

  if (isOnPremCompany(store)) {
    return { name: "help" };
  }

  defaults.sort((a, b) => a.meta.default - b.meta.default);
  const allowed = defaults.find((route) => isRouteAllowedFull(route, store));
  // nothing allowed -> 403 page and keep the original path to try again on page reload
  return allowed ? { name: allowed.name } : { name: "403", query: { path: to.fullPath } };
}

function isOnPremCompany(store) {
  return store.onPrem;
}

function isRouteAllowedFull(route, store) {
  return (
    isGeneralRouteAllowed(route, store) && (!isAuthRoute(route) || isAuthRouteAllowed(route, store))
  );
}

function isGeneralRouteAllowed(route, store) {
  return isRouteAllowedForCloud(route) && isRouteAllowedForSso(route, store);
}

function isAuthRouteAllowed(route, store) {
  return (
    isRouteAllowedForTraining(route, store) &&
    isRouteAllowedForOnPrem(route, store) &&
    isRouteAllowedForRole(route, store.user.role)
  );
}

function isAuthRoute(route) {
  return !!route.meta?.roles;
}

function isRouteAllowedForSso(route, store) {
  return !route.meta?.isSSO || store.isSSOEnabled;
}

function isRouteAllowedForCloud(route) {
  return route.meta?.isCloud === undefined || route.meta.isCloud === isCloud;
}

function isRouteAllowedForTraining(route, store) {
  return route.meta?.training !== false || !store.isTraining;
}

function isRouteAllowedForOnPrem(route, store) {
  return route.meta?.onPrem || !store.onPrem;
}

function isRouteAllowedForRole(route, role) {
  return route.meta?.roles?.includes(role);
}

function preserveQueryFeature(to, from) {
  // On initial load, from.query may be empty, so fallback to window.location
  const urlParams = new URLSearchParams(window.location.search);
  const fromFeature = from.query.feature || urlParams.getAll("feature");
  const toFeature = to.query.feature;
  if (fromFeature?.length && !toFeature) {
    return { ...to, query: { ...to.query, feature: fromFeature } };
  }
  // Return undefined to continue navigation
  return undefined;
}

function preserveQueryProject(to, from) {
  const paramsToPreserve = ["project", "mr"];
  let newQuery = null;

  // On initial load, from.query may be empty, so fallback to window.location
  const urlParams = new URLSearchParams(window.location.search);

  for (const param of paramsToPreserve) {
    const fromValue = from.query[param] || urlParams.get(param);
    const toValue = to.query[param];
    if (fromValue && !toValue) {
      newQuery = newQuery || { ...to.query };
      newQuery[param] = fromValue;
    }
  }

  if (newQuery) {
    return { ...to, query: newQuery };
  }
  return undefined;
}

/**
 * Add query parameter to first navigation from external source (external="true")
 * and remove it from all subsequent navigation events.
 * NOTE: This will also add 'external' to a page when refreshed
 *
 * This is used to eg. only autofocus when coming from an internal source
 */
function markExternalFrom(to, from) {
  const isExternal = !from.name;
  const isMarkedExternal = to.query.external;

  if (isExternal && !isMarkedExternal) {
    return { ...to, query: { ...to.query, external: "true" } };
  } else if (!isExternal && isMarkedExternal) {
    const query = { ...to.query };
    delete query.external;
    return { ...to, query };
  }
  // Return undefined to continue navigation
  return undefined;
}

async function openConnection(store) {
  if (!store.wsconnection.isOpen) {
    await wsconnect({ store });
  }
  if (!store.natsconnection) {
    // authserver decides the NATS subject scheme per company; the cookie it
    // set and the scheme we run must come from the same decision
    const { data } = await AuthService.isAuthenticated();
    store.setNatsScheme(data.nats_subject_scheme ?? "v1");
    // NATS is critical infra, gated like WAMP: await the connect so handlers
    // built after this guard route NATS. Auth-fatal NATS errors self-handle
    // (refresh/reload/logout) inside natsConnect; any other rejection bubbles to
    // the guard's catch and blocks the route.
    await natsConnect({ store });
  }
}
